import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { LifeBuoy, MapPinOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { AlertDeliveryTable } from '@/features/crashes/AlertDeliveryTable';
import { RelativeTime } from '@/components/domain/RelativeTime';
import { useAlerts } from '@/features/shared/queries';
import { resendAlerts } from '@/api/endpoints/alerts';
import { queryKeys } from '@/api/queryKeys';
import { errorMessage } from '@/api/errors';

/**
 * The responder console.
 *
 * Responders can read every alert in the system but have no access to devices,
 * telemetry, vehicles or the live stream — that is how the backend is scoped, and
 * this page does not paper over it. Where an owner sees a map, a responder sees an
 * explicit note that the location is not theirs to read, rather than an empty map
 * that looks broken or, worse, invented coordinates.
 */
export function RespondPage() {
  const alerts = useAlerts(300);
  const queryClient = useQueryClient();

  const resend = useMutation({
    mutationFn: (crashEventId: number) => resendAlerts(crashEventId),
    onSuccess: async () => {
      toast.success('Re-dispatch queued.');
      await queryClient.invalidateQueries({ queryKey: queryKeys.alerts(300) });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const grouped = (alerts.data ?? []).reduce<Map<number, typeof alerts.data>>((map, alert) => {
    const existing = map.get(alert.crash_event_id) ?? [];
    existing!.push(alert);
    map.set(alert.crash_event_id, existing);
    return map;
  }, new Map());

  const groups = [...grouped.entries()]
    .map(([crashEventId, group]) => ({
      crashEventId,
      group: group ?? [],
      failed: (group ?? []).filter((alert) => alert.status === 'failed').length,
      pending: (group ?? []).filter((alert) => alert.status === 'pending').length,
      latest: (group ?? []).reduce<string | null>(
        (newest, alert) => (!newest || (alert.sent_at ?? '') > newest ? (alert.sent_at ?? newest) : newest),
        null
      ),
    }))
    .sort((a, b) => {
      // Anything undelivered outranks everything else — those are the events where
      // somebody may still be waiting.
      const aUrgent = a.failed > 0 || a.pending > 0 ? 1 : 0;
      const bUrgent = b.failed > 0 || b.pending > 0 ? 1 : 0;
      if (aUrgent !== bUrgent) return bUrgent - aUrgent;
      return (b.latest ?? '').localeCompare(a.latest ?? '');
    });

  const needsAttention = groups.filter((group) => group.failed > 0).length;

  return (
    <>
      <PageHeader
        title="Response queue"
        description="Crash notifications across the system, undelivered first."
      />

      <Card className="mb-4">
        <CardBody className="flex items-start gap-2.5">
          <MapPinOff className="mt-0.5 size-4 shrink-0 text-ink-muted" aria-hidden />
          <p className="text-[13px] text-ink-secondary">
            Crash locations and live vehicle tracking are not available to the responder role. Open
            an event to see its dispatch record, or contact an administrator for location details.
          </p>
        </CardBody>
      </Card>

      {needsAttention > 0 && (
        <Card className="mb-4 border-bad/30 bg-bad-soft">
          <CardBody>
            <p className="text-sm font-medium text-ink">
              {needsAttention} event{needsAttention === 1 ? '' : 's'} with undelivered notifications
            </p>
            <p className="mt-0.5 text-[13px] text-ink-secondary">
              Re-sending only retries the channels that failed; anyone already reached is not called
              again.
            </p>
          </CardBody>
        </Card>
      )}

      {alerts.isPending ? (
        <Card>
          <SkeletonRows rows={5} />
        </Card>
      ) : alerts.isError ? (
        <ErrorState error={alerts.error} onRetry={alerts.refetch} />
      ) : groups.length === 0 ? (
        <Card>
          <EmptyState
            icon={LifeBuoy}
            title="Nothing in the queue"
            description="No crash notifications have been dispatched. This is the state you want."
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map(({ crashEventId, group, failed, pending }) => (
            <Card
              key={crashEventId}
              className={failed > 0 ? 'border-bad/30' : pending > 0 ? 'border-warn/30' : undefined}
            >
              <CardHeader
                title={`Crash event #${crashEventId}`}
                description={
                  failed > 0
                    ? `${failed} of ${group.length} notifications failed`
                    : pending > 0
                      ? `${pending} still sending`
                      : 'All notifications delivered'
                }
                action={
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/crashes/${crashEventId}`}
                      className="text-[13px] font-medium text-accent hover:text-accent-hover"
                    >
                      Details
                    </Link>
                    <Button
                      size="sm"
                      variant={failed > 0 ? 'primary' : 'secondary'}
                      loading={resend.isPending && resend.variables === crashEventId}
                      onClick={() => resend.mutate(crashEventId)}
                    >
                      <RefreshCw className="size-4" aria-hidden />
                      Re-send
                    </Button>
                  </div>
                }
              />
              <div className="border-b border-line px-4 py-2 text-[12px] text-ink-muted">
                Last attempt <RelativeTime value={group[0]?.sent_at ?? null} />
              </div>
              <AlertDeliveryTable alerts={group} />
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
